import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Presentation, Loader2, PlayCircle, Clock, ShieldCheck, Keyboard } from "lucide-react";
import ConsoleShell, { Breadcrumb } from "../ConsoleShell";
import { API, useAuth } from "../AuthContext";
import { DEMO_STEPS, DEMO_TOTAL_MIN } from "./demoSteps";

function Meta({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680]">{label}</div>
      <div className="text-[#0F1825] mt-0.5 leading-snug">{value}</div>
    </div>
  );
}

export default function ConsoleDemo() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const role = (user?.role || "").toLowerCase();
  const crumbs = role === "bd" ? [{ label: "Prospect Outreach", to: "/outreach" }, { label: "Client demo" }] : [{ label: "Engagements", to: "/console/engagements" }, { label: "Client demo" }];

  useEffect(() => {
    axios.get(`${API}/console/engagements`)
      .then(({ data }) => setItems((data.engagements || []).filter((e) => e.kind !== "live")))
      .catch(() => setItems([]));
  }, []);

  return (
    <ConsoleShell hideIllustrativeRibbon breadcrumb={<Breadcrumb items={crumbs} />}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="page-demo">
        <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
          <div className="max-w-2xl">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C84C21]">Business development · guided demo</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#0F1825] mt-3 leading-[1.1] tracking-tight">Walk a client through Voice2Evidence.</h1>
            <p className="mt-4 text-[#5C6470] text-[15px] leading-relaxed">
              Pick an illustrative case below. The real Client Workspace opens with a presenter panel that moves through the story step by step — <strong className="text-[#0F1825]">listening → PentaVoice™ five streams → triangulation → insight → advisory briefs</strong> — with what to say and what to point at on every step. Only simulated cases can be demoed; live client engagements never appear here.
            </p>
          </div>
          <div className="bg-white border border-[#E8E1D2] px-5 py-4 min-w-[260px]" data-testid="demo-facts">
            <div className="flex items-center gap-2 text-[13px] text-[#0F1825]"><Clock size={14} className="text-[#C84C21]" /> ~{DEMO_TOTAL_MIN} minutes · {DEMO_STEPS.length} steps</div>
            <div className="flex items-center gap-2 text-[13px] text-[#0F1825] mt-2"><Keyboard size={14} className="text-[#C84C21]" /> ← → to move · Esc to exit</div>
            <div className="flex items-center gap-2 text-[13px] text-[#0F1825] mt-2"><ShieldCheck size={14} className="text-[#C84C21]" /> Simulated data only</div>
          </div>
        </div>

        <ol className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E8E1D2] border border-[#E8E1D2]" data-testid="demo-outline">
          {DEMO_STEPS.map((s) => (
            <li key={s.key} className="bg-white px-4 py-3">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#C84C21]">{s.eyebrow}</div>
              <div className="text-[13px] text-[#0F1825] mt-1 leading-snug">{s.title}</div>
            </li>
          ))}
        </ol>

        <div className="mt-10 mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680] flex items-center gap-2"><Presentation size={12} /> Choose the case for this room</div>
        {!items && <div className="flex items-center gap-2 text-[#5C6470] text-[13px]"><Loader2 size={14} className="animate-spin" /> Loading cases…</div>}
        {items && items.length === 0 && <div className="text-[#5C6470] text-[13px]" data-testid="demo-empty">No illustrative engagements available.</div>}
        {items && items.length > 0 && (
          <ul className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="demo-cases">
            {items.map((e) => (
              <li key={e.id} className="bg-white border border-[#E8E1D2] p-6 flex flex-col" data-testid={`demo-case-${e.id}`}>
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6470]">{e.client_label}</span>
                  {e.demo_category && <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#C84C21]">{e.demo_category}{e.demo_category_detail ? ` · ${e.demo_category_detail}` : ""}</span>}
                </div>
                <h3 className="font-serif text-2xl text-[#0F1825] leading-snug mt-1">{e.brand}</h3>
                <div className="text-[13.5px] text-[#5C6470] mt-1">{[e.asset, e.mechanism].filter(Boolean).join(" · ")}</div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-3 text-[12.5px]">
                  <Meta label="Therapeutic area" value={e.ta} />
                  <Meta label="Indication" value={e.indication} />
                  <Meta label="Lifecycle" value={e.lifecycle_phase} />
                </div>
                <div className="mt-5 pt-4 border-t border-[#F0EBDC] flex items-center gap-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#5C6470]"><span className="text-[#0F1825] font-medium">{e.signal_count}</span> signals</span>
                  <Link to={`/console/engagements/${e.id}?demo=1&step=0`} className="ml-auto inline-flex items-center gap-2 bg-[#0F1825] hover:bg-[#C84C21] text-white font-mono text-[10.5px] uppercase tracking-[0.18em] px-4 py-2.5 transition-colors" data-testid={`demo-start-${e.id}`}>
                    <PlayCircle size={13} /> Start guided demo
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ConsoleShell>
  );
}
